<template>
  <div
    :class="[
      'notification-item relative p-4 border-b border-gray-100 transition-all cursor-pointer',
      !notification.read ? 'bg-blue-50' : 'bg-white',
      isDeleting ? 'opacity-50 pointer-events-none' : '',
      hoverClass
    ]"
    @click="handleClick"
  >
    <!-- Unread Indicator -->
    <div
      v-if="!notification.read"
      class="absolute left-0 top-0 bottom-0 w-1 bg-primary"
    ></div>

    <div class="flex gap-3">
      <!-- Notification Icon -->
      <div class="flex-shrink-0">
        <div
          :class="[
            'w-12 h-12 rounded-full flex items-center justify-center',
            getIconBgColor()
          ]"
        >
          <ImgComponent
            fallback="/images/all/bell.svg"
            :src="notification.icon"
            :alt="notification.type"
            class="w-6 h-6"
          />
        </div>
      </div>

      <!-- Notification Content -->
      <div class="flex-1 min-w-0">
        <!-- Title & Date -->
        <div class="flex justify-between items-start gap-2 mb-1">
          <h4
            :class="[
              'font-semibold text-sm line-clamp-1',
              !notification.read ? 'text-gray-900' : 'text-gray-600'
            ]"
          >
            {{ notification.title }}
          </h4>
          <span class="text-xs text-gray-400 whitespace-nowrap">
            {{ formattedDate }}
          </span>
        </div>

        <!-- Message -->
        <p
          :class="[
            'text-sm line-clamp-2 mb-2',
            !notification.read ? 'text-gray-700' : 'text-gray-500'
          ]"
        >
          {{ notification.message }}
        </p>

        <!-- Action Button (if applicable) -->
        <button
          v-if="notification.action && notification.actionUrl"
          @click.stop="handleActionClick"
          class="text-primary text-sm font-medium hover:underline"
        >
          {{ notification.action }}
        </button>
      </div>

      <!-- Delete Button -->
      <div class="flex-shrink-0">
        <button
          @click.stop="handleDelete"
          :class="[
            'w-8 h-8 rounded-full flex items-center justify-center transition-all',
            'hover:bg-red-100 hover:text-red-600 text-gray-400'
          ]"
          :disabled="isDeleting"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Priority Indicator (if high priority) -->
    <div
      v-if="notification.priority === 'high'"
      class="absolute top-2 right-12"
    >
      <span
        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
      >
        Urgent
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import { formatNotificationDate } from '../serializer';

const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['click', 'delete']);

const isDeleting = ref(false);
const hoverClass = ref('');

const formattedDate = computed(() => {
  return formatNotificationDate(props.notification.createdAt);
});

const handleClick = () => {
  // Mark as read if not already read
  if (!props.notification.read) {
    bus.emit(
      constants.EVENTS.INBOX.NOTIFICATION_MARK_READ,
      props.notification.id
    );
  }

  // Emit click event for parent to open dialog
  emit('click', props.notification);
};

const handleActionClick = () => {
  bus.emit('navigation:requested', { url: props.notification.actionUrl });
};

const handleDelete = () => {
  if (isDeleting.value) return;

  isDeleting.value = true;
  bus.emit(constants.EVENTS.INBOX.NOTIFICATION_DELETE, props.notification.id);

  // Emit delete event for parent
  emit('delete', props.notification.id);
};

const handleIconError = (event) => {
  // Fallback to default icon if custom icon fails to load
  event.target.src = '/images/icons/notification-default.svg';
};

const getIconBgColor = () => {
  const colorMap = {
    order: 'bg-blue-100',
    promotion: 'bg-green-100',
    system: 'bg-gray-100',
    payment: 'bg-yellow-100',
    delivery: 'bg-purple-100'
  };

  return colorMap[props.notification.type] || 'bg-gray-100';
};
</script>

<style scoped>
.notification-item {
  position: relative;
}

.notification-item:hover {
  background-color: #f9fafb;
}

.notification-item:not(.read):hover {
  background-color: #eff6ff;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
